import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ArrowUpRight, HeartHandshake, Building2, HandCoins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

const TYPES = [
  { id: "assistance", label: "Get Assistance", icon: HeartHandshake, blurb: "I or someone I know needs help.", accent: "#7C3AED" },
  { id: "partner", label: "Partner With Us", icon: Building2, blurb: "We're a nonprofit or municipality.", accent: "#C9A227" },
  { id: "donor", label: "Donate · Support", icon: HandCoins, blurb: "I want to fund the work financially.", accent: "#1E40AF" },
];

export default function AssistanceAndDonate() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "",
    inquiry_type: "assistance", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) { toast.error("Please include a brief message (10+ characters)."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form, { withCredentials: false });
      toast.success("Received. The Alliance will respond within 2 business days.");
      setForm({ ...form, name: "", email: "", organization: "", role: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  const isAssist = form.inquiry_type === "assistance";
  const active = TYPES.find((t) => t.id === form.inquiry_type);

  return (
    <section
      id="assist"
      data-testid="assist-section"
      className="relative bg-white py-24 sm:py-32 border-b border-[#E8E5DA]"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8" id="donate">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#C9A227] mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-[#C9A227]" /> §04 · Engage the Alliance
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] text-[#0A0612] mb-6">
              {isAssist ? "Need help today?" : form.inquiry_type === "donor" ? "Stand with the work." : "Build with us."}
            </h2>
            <p className="text-base leading-relaxed text-[#0A0612]/80 mb-10 max-w-md">
              {isAssist
                ? "Tell us your situation. A Bridge Specialist from the Alliance will reach out within 48 hours — confidential, no obligation."
                : "Whether you bring funding, jobs, or referrals — there is a defined place in the Alliance for partners who want to serve the Last, the Least and the Lost with a credible, measurable outcome."}
            </p>

            <div className="space-y-5">
              <ContactBlock label="Resident Intake · Bridge Pipeline"   line="heather.a@theexchangealliance.com" />
              <ContactBlock label="Partnerships · Municipalities"        line="tea.info.help@theexchangealliance.com" />
              <ContactBlock label="Employer Workforce Pipeline"          line="will.s@theexchangealliance.com" />
              <ContactBlock label="Development · Donors"                 line="andrew.m@theexchangealliance.com" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="inquiry-form"
              className="bg-white border border-[#E8E5DA] p-8 sm:p-10 relative shadow-sm">
              {/* Active accent top bar */}
              <div className="absolute top-0 left-0 right-0 h-1 transition-colors" style={{ background: active?.accent || "#C9A227" }} />

              <div className="mb-8">
                <Label className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-3 block">
                  I am reaching out as
                </Label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    const isActive = form.inquiry_type === t.id;
                    return (
                      <button key={t.id} type="button" data-testid={`inquiry-type-${t.id}`}
                        onClick={() => setForm((p) => ({ ...p, inquiry_type: t.id }))}
                        className={`p-4 text-left transition-all border ${
                          isActive ? "border-[#0A0612] bg-[#FAFAF7] shadow-sm" : "border-[#E8E5DA] bg-white hover:border-[#0A0612]/40"
                        }`}
                        style={isActive ? { borderTopColor: t.accent, borderTopWidth: 3 } : {}}>
                        <Icon className="h-4 w-4 mb-2" strokeWidth={1.6} style={{ color: t.accent }} />
                        <div className="font-mono text-[10px] tracking-[0.18em] uppercase font-semibold text-[#0A0612]">{t.label}</div>
                        <div className="text-[11px] mt-1 text-[#6B6F76]">{t.blurb}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field id="name" label={isAssist ? "Your Name (or referrer's)" : "Full Name"} value={form.name} onChange={set("name")} required testid="field-name" />
                <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} required testid="field-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field id="organization" label={isAssist ? "City / Location" : "Organization"} value={form.organization} onChange={set("organization")} required testid="field-organization" />
                <Field id="role" label={isAssist ? "Relationship" : "Title / Role"} value={form.role} onChange={set("role")} required testid="field-role" />
              </div>

              <div className="mb-8">
                <Label htmlFor="message" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-2 block">
                  {isAssist ? "What's happening?" : "Message"}
                </Label>
                <Textarea id="message" data-testid="field-message"
                  value={form.message} onChange={set("message")} required rows={5}
                  placeholder={isAssist
                    ? "A few sentences about the situation — current housing, immediate needs, contact preference."
                    : "Briefly describe your jurisdiction, scope, or partnership interest."}
                  className="rounded-none bg-[#FAFAF7] border-[#E8E5DA] text-[#0A0612] placeholder:text-[#6B6F76] focus-visible:ring-0 focus-visible:border-[#0A0612] font-sans text-base resize-none" />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-[#E8E5DA]">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] max-w-xs">
                  All inquiries confidential · HIPAA scoped routing
                </div>
                <Button type="submit" disabled={loading} data-testid="inquiry-submit"
                  className="rounded-none bg-[#0A0612] hover:bg-[#1A1A1A] text-[#F4D88A] font-mono text-xs tracking-[0.2em] uppercase h-12 px-6 group disabled:opacity-50 ring-1 ring-[#D4AF37]/30">
                  {loading ? "Transmitting…" : "Submit Securely"}
                  {!loading && <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-2 block">{label}</Label>
      <Input id={id} type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="rounded-none bg-[#FAFAF7] border-[#E8E5DA] text-[#0A0612] placeholder:text-[#6B6F76] focus-visible:ring-0 focus-visible:border-[#0A0612] font-sans text-base h-11" />
    </div>
  );
}

function ContactBlock({ label, line }) {
  // Render the address as a live `mailto:` link so a click opens the user's
  // default mail client pre-addressed to the alliance directory inbox.
  const slug = String(label).toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return (
    <div className="border-l-2 border-[#C9A227] pl-4">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1">{label}</div>
      <a
        href={`mailto:${line}`}
        data-testid={`contact-mail-${slug}`}
        className="font-display text-base font-medium tracking-tight text-[#0A0612] hover:text-[#C9A227] underline-offset-4 hover:underline break-all"
      >
        {line}
      </a>
    </div>
  );
}
